import { Avatar, Box, Button, Chip, Grid, Skeleton, Tab, Tabs, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from 'features/users/stores/userSlice'
import { useEffect, useState } from 'react'
import useFetchMisEvaluados from '../hooks/useFetchMisEvaluados'
import EvaluadosCard from './EvaluadosCard'
import EvaluadosSimpleTable from './EvaluadosSimpleTable'

const GROUP_CONFIG = {
  yo: { label: 'Autoevaluación', order: 0 },
  superiores: { label: 'Superiores', order: 1 },
  pares: { label: 'Pares', order: 2 },
  inferiores: { label: 'A mi cargo', order: 3 },
  otros: { label: 'Otros', order: 4 },
}

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
)

const MisEvaluados = ({
  usuarioWeb,
  hideNav = false,
  onEvaluadoClick,
  onBackToEvaluaciones,
  evaluador = null,
  onBack,
}) => {
  const [Ad, setAd] = useState(false)
  const usuario = useSelector(selectCurrentUser)
  const [evaluadosPorGrupo, setEvaluadosPorGrupo] = useState({})
  const [activeTab, setActiveTab] = useState(0)

  const is_admin = usuario?.admin_evaluaciones === 'S'

  const { response, loading, error } = useFetchMisEvaluados({
    id: usuarioWeb,
    Ad,
    evaluadorWeb: evaluador?.usuario_web,
  })

  useEffect(() => {
    if (response) {
      setEvaluadosPorGrupo(response)
      setActiveTab(0)
    }
  }, [response])

  const activeGroups = Object.entries(evaluadosPorGrupo)
    .filter(([, evaluados]) => evaluados && evaluados.length > 0)
    .sort(([a], [b]) => (GROUP_CONFIG[a]?.order ?? 99) - (GROUP_CONFIG[b]?.order ?? 99))

  if (error) {
    return <Typography>Error: {error}</Typography>
  }

  const [apellidosEval = '', nombreEval = ''] = (evaluador?.nombre ?? '').split(', ')

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {evaluador && typeof onBack === 'function' && (
            <Button variant="outlined" onClick={onBack}>
              Volver a Evaluadores
            </Button>
          )}
          {!hideNav && (
            <Button variant="contained" color="primary" onClick={onBackToEvaluaciones}>
              Volver a Evaluaciones
            </Button>
          )}
        </Box>
        {is_admin && !evaluador && (
          <Button variant="contained" onClick={() => setAd(!Ad)}>
            {Ad ? 'Vista normal' : 'Vista administrador'}
          </Button>
        )}
      </Box>
      {evaluador && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            mb: 2,
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
            backgroundColor: 'primary.50',
          }}
        >
          <Avatar src={`/assets/images/${evaluador.imagen}`} sx={{ width: 56, height: 56 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
              {apellidosEval}
            </Typography>
            <Typography
              variant="caption"
              sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}
            >
              {nombreEval}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {evaluador.puestos?.[0]?.descripcion ?? '—'}
            </Typography>
          </Box>
          <Chip label="Evaluador" color="primary" size="small" />
        </Box>
      )}
      {loading ? (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {[120, 90, 70, 80, 60].map((w, i) => (
              <Skeleton key={i} variant="rounded" width={w} height={36} />
            ))}
          </Box>

          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Grid container spacing={2}>
              {Array.from({ length: 6 }, (_, i) => (
                <Grid
                  key={i}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                    xl: 3,
                  }}
                >
                  <EvaluadosCard loading />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <EvaluadosSimpleTable evaluados={[]} loading={true} onEvaluadoClick={() => {}} />
          </Box>
        </Box>
      ) : (
        <>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {activeGroups.map(([groupKey, evaluados]) => (
              <Tab
                key={groupKey}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {GROUP_CONFIG[groupKey]?.label ?? groupKey}
                    <Chip label={evaluados.length} size="small" />
                  </Box>
                }
              />
            ))}
          </Tabs>
          {activeGroups.map(([groupKey, evaluados], index) => (
            <TabPanel key={groupKey} value={activeTab} index={index}>
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Grid container spacing={2}>
                  {evaluados.map((evaluado) => (
                    <Grid
                      key={evaluado.id}
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4,
                        xl: 3,
                      }}
                    >
                      <EvaluadosCard
                        evaluado={evaluado}
                        onEvaluadoClick={onEvaluadoClick}
                        onBackToEvaluaciones={onBackToEvaluaciones}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <EvaluadosSimpleTable
                  evaluados={evaluados}
                  loading={false}
                  onEvaluadoClick={onEvaluadoClick}
                />
              </Box>
            </TabPanel>
          ))}
        </>
      )}
    </Box>
  )
}

export default MisEvaluados
